import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Outlet, useLocation } from 'react-router-dom';

import { ErrorBoundary } from './components/ErrorBoundary';
import { Layout } from './components/Layout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { SkeletonLoader } from './components/SkeletonLoader';
import { SuperAdminRoute } from './components/SuperAdminRoute';
import { EarlyAccessBanner } from './components/EarlyAccessBanner';
import { Toaster } from './components/Toaster';
import { I18nProvider } from './i18n/provider';
import { applySeoMeta, SEO_SITE_ORIGIN } from './lib/seo';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Keys = lazy(() => import('./pages/Keys'));
const Billing = lazy(() => import('./pages/Billing'));
const Webhooks = lazy(() => import('./pages/Webhooks'));
const BillingSuccess = lazy(() => import('./pages/BillingSuccess'));
const BillingCancel = lazy(() => import('./pages/BillingCancel'));
const Security = lazy(() => import('./pages/Security'));
const Playground = lazy(() => import('./pages/Playground'));
const Landing = lazy(() => import('./pages/Landing'));
const TeamManagement = lazy(() => import('./pages/TeamManagement'));
const AuditLog = lazy(() => import('./pages/AuditLog'));
const NotificationCenter = lazy(() => import('./pages/NotificationCenter'));
const InviteAccept = lazy(() => import('./pages/InviteAccept'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const NotFound = lazy(() => import('./pages/NotFound'));
const OwnerStaff = lazy(() => import('./pages/owner/Staff'));
const BlogManager = lazy(() => import('./pages/owner/BlogManager'));
const BlogEditor = lazy(() => import('./pages/owner/BlogEditor'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));

// Super Admin pages
const SuperAdminDashboard = lazy(() =>
  import('./pages/superadmin/Dashboard').then((m) => ({ default: m.SuperAdminDashboard })),
);
const SuperAdminUsers = lazy(() =>
  import('./pages/superadmin/Users').then((m) => ({ default: m.SuperAdminUsers })),
);
const SuperAdminUserDetail = lazy(() =>
  import('./pages/superadmin/UserDetail').then((m) => ({ default: m.SuperAdminUserDetail })),
);
const SuperAdminDemoRequests = lazy(() =>
  import('./pages/superadmin/DemoRequests').then((m) => ({ default: m.SuperAdminDemoRequests })),
);
const SuperAdminSecurity = lazy(() =>
  import('./pages/superadmin/Security'),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      retry: 1,
    },
  },
});

const WWW_HOST = 'www.walletgate.app';
const ROOT_HOST = 'walletgate.app';
const CONSOLE_HOST = 'console.walletgate.app';
const AUTH_PATH_PREFIXES = ['/admin', '/login', '/register', '/signup', '/forgot-password', '/reset-password'] as const;

function isLocalHost(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost');
}

function isAuthOrAdminPath(pathname: string): boolean {
  return AUTH_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

function DomainRouteGuard(): null {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const { hostname } = window.location;
    if (isLocalHost(hostname)) return;

    const path = location.pathname;
    const suffix = `${location.pathname}${location.search}${location.hash}`;
    const onRoot = hostname === ROOT_HOST;
    const onWww = hostname === WWW_HOST;
    const onConsole = hostname === CONSOLE_HOST;
    if (!onRoot && !onWww && !onConsole) return;

    if (onRoot) {
      if (isAuthOrAdminPath(path)) {
        window.location.replace(`https://${CONSOLE_HOST}${suffix}`);
      } else {
        window.location.replace(`https://${WWW_HOST}${suffix}`);
      }
      return;
    }

    if (onWww && isAuthOrAdminPath(path)) {
      window.location.replace(`https://${CONSOLE_HOST}${suffix}`);
      return;
    }

    // Console subdomain: allow all routes to render normally.
    // Public pages (landing, blog, privacy, terms) are accessible when logged out.
    // Protected routes (/admin/*) are guarded by <ProtectedRoute>.
  }, [location.pathname, location.search, location.hash]);

  return null;
}

function ScrollToTop(): null {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function RouteSeoMeta(): null {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    const isAuthPath = path === '/login' || path === '/register' || path === '/signup' || path === '/forgot-password' || path === '/reset-password';
    const isAdminPath = path.startsWith('/admin');
    const shouldNoIndex = isAuthPath || isAdminPath;
    applySeoMeta({
      robots: shouldNoIndex
        ? 'noindex, nofollow'
        : 'index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1',
      canonical: `${SEO_SITE_ORIGIN}${path}`,
    });
  }, [location.pathname]);

  return null;
}

function AppRoutes(): JSX.Element {
  return (
    <Suspense fallback={<SkeletonLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/signup" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/invite/accept" element={<InviteAccept />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />

        {/* Admin protected routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <Layout>
                <Outlet />
              </Layout>
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="keys" element={<Keys />} />
          <Route path="playground" element={<Playground />} />
          <Route path="billing" element={<Billing />} />
          <Route path="billing/success" element={<BillingSuccess />} />
          <Route path="billing/cancel" element={<BillingCancel />} />
          <Route path="webhooks" element={<Webhooks />} />
          <Route path="security" element={<Security />} />
          <Route path="team" element={<TeamManagement />} />
          <Route path="audit-log" element={<AuditLog />} />
          <Route path="notifications" element={<NotificationCenter />} />
          <Route path="owner" element={<OwnerStaff />} />
          <Route path="blog" element={<BlogManager />} />
          <Route path="blog/new" element={<BlogEditor />} />
          <Route path="blog/:id/edit" element={<BlogEditor />} />

          {/* Super Admin routes */}
          <Route
            path="superadmin"
            element={
              <SuperAdminRoute>
                <SuperAdminDashboard />
              </SuperAdminRoute>
            }
          />
          <Route
            path="superadmin/users"
            element={
              <SuperAdminRoute>
                <SuperAdminUsers />
              </SuperAdminRoute>
            }
          />
          <Route
            path="superadmin/users/:id"
            element={
              <SuperAdminRoute>
                <SuperAdminUserDetail />
              </SuperAdminRoute>
            }
          />
          <Route
            path="superadmin/demo-requests"
            element={
              <SuperAdminRoute>
                <SuperAdminDemoRequests />
              </SuperAdminRoute>
            }
          />
          <Route
            path="superadmin/security"
            element={
              <SuperAdminRoute>
                <SuperAdminSecurity />
              </SuperAdminRoute>
            }
          />
        </Route>

        {/* 404 Not Found */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

export function App(): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <ErrorBoundary>
            <ScrollToTop />
            <DomainRouteGuard />
            <RouteSeoMeta />
            <EarlyAccessBanner />
            <AppRoutes />
            <Toaster />
          </ErrorBoundary>
        </BrowserRouter>
      </I18nProvider>
    </QueryClientProvider>
  );
}
